import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import need from "../../../apiData/need.json";
import {
  Section, BgBlob, BgMesh, Inner,
  HeaderWrap, Eyebrow, SectionTitle, TitleUnderline, SectionDesc,
  StatsStrip, StatCard, StatNumber, StatLabel,
  CardsGrid, FeatureCard, IconWrap, FeatureImg, FeatureEmoji,
  FeatureTitle, FeatureText, CardLink,
  QuoteBand, QuoteOrb, QuoteGlyph, QuoteText, QuoteCta,
} from "./Need.styles";

// ─── Static stats — visible brand proof points ─────────────────────────────────
// Update these values to match real YogaShastra numbers.

const STATS = [
  { number: "2,400+",  label: "Students Trained"   },
  { number: "8+",   label: "Years of Experience" },
  { number: "50+",   label: "Yoga Practices"      },
  { number: "98%",   label: "Satisfaction Rate"   },
];

// ─── Fallback icons when images are missing ────────────────────────────────────

const FALLBACK_ICONS = ["🧘", "🌿", "🪷", "☀️", "🌊", "✨", "💫", "🏆"];

// ─── Icon atoms ────────────────────────────────────────────────────────────────

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// ─── FeatureItem ───────────────────────────────────────────────────────────────

const FeatureItem = ({ item, index }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <FeatureCard $index={index} aria-label={item.name}>
      {/* Floating icon */}
      <IconWrap $delay={index} aria-hidden="true">
        {item.image && !imgError ? (
          <FeatureImg
            src={item.image}
            alt=""
            onError={() => setImgError(true)}
          />
        ) : (
          <FeatureEmoji>
            {FALLBACK_ICONS[index % FALLBACK_ICONS.length]}
          </FeatureEmoji>
        )}
      </IconWrap>

      <FeatureTitle>{item.name}</FeatureTitle>
      <FeatureText>{item.comments}</FeatureText>

      {/* Hover-reveal link */}
      <CardLink aria-hidden="true">
        Discover more <ArrowIcon />
      </CardLink>
    </FeatureCard>
  );
};

// ─── Need ──────────────────────────────────────────────────────────────────────

const Need = () => {
  const navigate = useNavigate();

  const handleCtaClick = useCallback(() => {
    navigate("/contactUs");
  }, [navigate]);

  return (
    <Section id="need">
      {/* Background decoration */}
      <BgBlob className="b1" aria-hidden="true" />
      <BgBlob className="b2" aria-hidden="true" />
      <BgBlob className="b3" aria-hidden="true" />
      <BgMesh aria-hidden="true" />

      <Inner>

        {/* ── Header ── */}
        <HeaderWrap>
          <Eyebrow>✦ Our Difference</Eyebrow>
          <SectionTitle>
            The <em>YogaShastra</em> Difference
          </SectionTitle>
          <TitleUnderline aria-hidden="true" />
          <SectionDesc>
            We go beyond physical postures. Our approach blends ancient wisdom
            with modern science — creating a transformative experience that
            nurtures your body, sharpens your mind, and uplifts your spirit.
          </SectionDesc>
        </HeaderWrap>

        {/* ── Social proof stats ── */}
        <StatsStrip aria-label="Key statistics">
          {STATS.map((stat) => (
            <StatCard key={stat.label}>
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsStrip>

        {/* ── Feature cards ── */}
        <CardsGrid>
          {need?.map((item, i) => (
            <FeatureItem
              key={`${item.name}_${i}`}
              item={item}
              index={i}
            />
          ))}
        </CardsGrid>

        {/* ── Closing quote + CTA ── */}
        <QuoteBand>
          <QuoteOrb className="o1" aria-hidden="true" />
          <QuoteOrb className="o2" aria-hidden="true" />
          <QuoteGlyph aria-hidden="true">"</QuoteGlyph>
          <QuoteText>
            Yoga does not just change the way we see things, it transforms the
            person who sees. Join YogaShastra and discover the life-changing
            power of an authentic practice.
          </QuoteText>
          <QuoteCta
            type="button"
            onClick={handleCtaClick}
            aria-label="Request a free consultation with YogaShastra"
          >
            Request a Free Consultation <ArrowIcon />
          </QuoteCta>
        </QuoteBand>

      </Inner>
    </Section>
  );
};

Need.displayName = "Need";
export default React.memo(Need);